import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, cx } from './ui.tsx';

/**
 * Application dialogs, replacing window.confirm and window.alert.
 *
 * The browser's own dialogs were doing this and were wrong for a finance tool
 * in three ways: they look like the operating system rather than the product,
 * they block the whole tab so nothing can render behind them, and — the one
 * that matters — they give a destructive action the same weight as an
 * innocuous one. "Delete this ledger" and "Rename this file" should not be the
 * same grey box with the same two buttons.
 *
 * Promise-based so calling code reads the way the old one did:
 *
 *     if (!(await confirm({ title: 'Delete?', tone: 'danger' }))) return;
 */

export interface ConfirmOptions {
  title: string;
  /** Body text. Kept to a sentence or two — a dialog is not documentation. */
  message?: string | undefined;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  tone?: 'default' | 'danger' | undefined;
  /** Extra detail listed under the message, e.g. what will be removed. */
  details?: readonly string[] | undefined;
}

export interface PromptOptions {
  title: string;
  message?: string | undefined;
  label: string;
  initialValue?: string | undefined;
  placeholder?: string | undefined;
  confirmLabel?: string | undefined;
  /** `password` masks the field — used when re-proving identity. */
  type?: 'text' | 'password' | undefined;
  tone?: 'default' | 'danger' | undefined;
  /** Return a message to block submission, or null to allow it. */
  validate?: ((value: string) => string | null) | undefined;
}

interface DialogApi {
  confirm(options: ConfirmOptions): Promise<boolean>;
  prompt(options: PromptOptions): Promise<string | null>;
  /** An acknowledgement with a single button. */
  notify(options: { title: string; message?: string; tone?: 'default' | 'danger' }): Promise<void>;
  /**
   * Arbitrary content — used by Properties and the folder picker.
   *
   * `onDismiss` fires when the dialog is closed without the content having
   * finished — Escape, or a click on the backdrop. Custom content that has a
   * caller waiting on it must pass one, or a dismissal leaves that caller
   * waiting for ever.
   */
  open(
    render: (close: () => void) => ReactNode,
    options?: {
      onDismiss?: () => void;
      /**
       * A panel wide enough for two columns.
       *
       * For content that is a place to look around rather than a form to
       * fill in — the folder tree beside what is filed in it. A form gets no
       * better for being stretched, which is why this is asked for rather
       * than given.
       */
      wide?: boolean;
    },
  ): void;
}

const DialogContext = createContext<DialogApi | null>(null);

export function useDialog(): DialogApi {
  const api = useContext(DialogContext);
  if (!api) throw new Error('useDialog must be used inside <DialogProvider>');
  return api;
}

type Request =
  | { kind: 'confirm'; options: ConfirmOptions; resolve(value: boolean): void }
  | { kind: 'prompt'; options: PromptOptions; resolve(value: string | null): void }
  | {
      kind: 'notify';
      options: { title: string; message?: string; tone?: 'default' | 'danger' };
      resolve(): void;
    }
  | {
      kind: 'custom';
      render(close: () => void): ReactNode;
      onDismiss?: (() => void) | undefined;
      wide?: boolean | undefined;
    };

export function DialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);

  const api = useMemo<DialogApi>(
    () => ({
      confirm: (options) =>
        new Promise<boolean>((resolve) => setRequest({ kind: 'confirm', options, resolve })),
      prompt: (options) =>
        new Promise<string | null>((resolve) => setRequest({ kind: 'prompt', options, resolve })),
      notify: (options) =>
        new Promise<void>((resolve) => setRequest({ kind: 'notify', options, resolve })),
      open: (render, options) =>
        setRequest({ kind: 'custom', render, onDismiss: options?.onDismiss, wide: options?.wide }),
    }),
    [],
  );

  const dismiss = useCallback(() => {
    setRequest((current) => {
      // Dismissing always resolves — an unresolved promise would leave the
      // caller waiting for ever on a dialog that is no longer on screen.
      if (current?.kind === 'confirm') current.resolve(false);
      if (current?.kind === 'prompt') current.resolve(null);
      if (current?.kind === 'notify') current.resolve();
      // Custom content resolves through a callback of its own, since the
      // provider has no promise of its own to settle for it.
      if (current?.kind === 'custom') current.onDismiss?.();
      return null;
    });
  }, []);

  return (
    <DialogContext.Provider value={api}>
      {children}
      {request ? (
        <DialogHost request={request} onDismiss={dismiss} setRequest={setRequest} />
      ) : null}
    </DialogContext.Provider>
  );
}

function DialogHost({
  request,
  onDismiss,
  setRequest,
}: {
  request: Request;
  onDismiss(): void;
  setRequest(value: Request | null): void;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState(
    request.kind === 'prompt' ? (request.options.initialValue ?? '') : '',
  );
  const [error, setError] = useState<string | null>(null);

  // Focus moves into the dialog on open and the page behind stops scrolling,
  // so a keyboard user is never left tabbing around underneath it.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const target =
      panel.current?.querySelector<HTMLElement>('input, textarea, [data-autofocus]') ??
      panel.current?.querySelector<HTMLElement>('button');
    target?.focus();

    return () => {
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onDismiss();
      return;
    }

    if (event.key !== 'Tab' || !panel.current) return;

    // A modal that lets focus escape behind it is not modal.
    const focusable = [
      ...panel.current.querySelectorAll<HTMLElement>(
        'button, input, textarea, select, a[href], [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((element) => !element.hasAttribute('disabled'));

    if (focusable.length === 0) return;
    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function submitPrompt() {
    if (request.kind !== 'prompt') return;

    // Names get trimmed — a folder called "Invoices " is a mistake every
    // time. Passwords do not: a space at either end is a legitimate
    // character, and silently removing it turns a correct password into a
    // rejected one with no explanation.
    const submitted = request.options.type === 'password' ? value : value.trim();

    const problem =
      request.options.validate?.(submitted) ?? (submitted ? null : 'This cannot be empty.');
    if (problem) {
      setError(problem);
      return;
    }
    request.resolve(submitted);
    setRequest(null);
  }

  const danger = request.kind !== 'custom' && request.options.tone === 'danger';

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto overscroll-contain bg-[rgba(12,26,43,0.45)] p-4 sm:items-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        className={cx(
          /*
           * Never taller than the screen, and scrolling inside when it would
           * be.
           *
           * The panel had a width and no height, so a form with four role
           * cards in it simply ran off the top and bottom of the window —
           * the heading and the Save button both out of reach, with nothing
           * to scroll because the backdrop was a fixed, centred box. On a
           * laptop screen that is most of the dialogs in the system.
           *
           * Dynamic viewport units, so the mobile browser chrome sliding
           * away does not leave the buttons under it.
           */
          'my-auto flex max-h-[calc(100dvh-2rem)] w-full flex-col',
          'rounded-xl border border-border-strong bg-surface shadow-2xl',
          /*
           * Exactly one max-width, chosen here rather than layered over a
           * base one. Both were being emitted before — `max-w-md` and then
           * `max-w-2xl` for a custom dialog — and which of the two won was
           * decided by the order Tailwind happened to write them into the
           * stylesheet, not by the order they appear here. `max-w-md` won, so
           * every custom dialog in the system had been rendering at 448px
           * instead of the width it was laid out for, which is why Format
           * Cells grew a horizontal scrollbar and lost its Sample.
           */
          // `wide` is Excel's Format Cells width, not "as wide as it will
          // go": at max-w-5xl the same content spread over 1024px and left a
          // Sample box the size of a postcard with nothing in it.
          request.kind === 'custom' ? (request.wide ? 'max-w-[52rem]' : 'max-w-2xl') : 'max-w-md',
        )}
      >
        {request.kind === 'custom' ? (
          // Custom content brings its own layout, so it is given the whole
          // panel and the scrolling that comes with it.
          <div className="flex min-h-0 flex-col overflow-y-auto">
            {request.render(() => setRequest(null))}
          </div>
        ) : (
          // Capped like the custom branch, or a long list of details would
          // spill out past the rounded corners instead of scrolling.
          <div className="flex min-h-0 flex-col overflow-y-auto">
            <div className="flex items-start gap-3 px-5 pb-3 pt-5">
              {danger ? (
                <span
                  aria-hidden
                  className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-danger-bg text-[var(--danger)]"
                >
                  <WarningGlyph />
                </span>
              ) : null}
              <div className="min-w-0">
                <h2 id="dialog-title" className="text-h3 text-ink">
                  {request.options.title}
                </h2>
                {request.options.message ? (
                  <p className="mt-1 text-body-sm leading-relaxed text-body">
                    {request.options.message}
                  </p>
                ) : null}
              </div>
            </div>

            {request.kind === 'confirm' && request.options.details?.length ? (
              <ul className="mx-5 mb-3 list-disc rounded-md bg-sunken px-8 py-2.5 text-meta text-body">
                {request.options.details.map((detail) => (
                  <li key={detail} className="py-0.5">
                    {detail}
                  </li>
                ))}
              </ul>
            ) : null}

            {request.kind === 'prompt' ? (
              <div className="px-5 pb-1">
                <label className="block text-meta font-medium text-body" htmlFor="dialog-input">
                  {request.options.label}
                </label>
                <input
                  id="dialog-input"
                  type={request.options.type ?? 'text'}
                  autoComplete={
                    request.options.type === 'password' ? 'current-password' : undefined
                  }
                  value={value}
                  placeholder={request.options.placeholder ?? ''}
                  onChange={(event) => {
                    setValue(event.target.value);
                    setError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      submitPrompt();
                    }
                  }}
                  className={cx(
                    'mt-1.5 h-control w-full rounded-md border bg-surface px-3 text-body-sm text-ink',
                    error ? 'border-[var(--danger)]' : 'border-border-input',
                  )}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'dialog-error' : undefined}
                />
                {error ? (
                  <p id="dialog-error" className="mt-1 text-micro text-[var(--danger)]">
                    {error}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 px-5 pb-5 pt-4">
              {request.kind !== 'notify' ? (
                <Button onClick={onDismiss}>
                  {request.kind === 'confirm'
                    ? (request.options.cancelLabel ?? 'Cancel')
                    : 'Cancel'}
                </Button>
              ) : null}

              <Button
                variant={danger ? 'danger' : 'primary'}
                data-autofocus={request.kind === 'notify' ? '' : undefined}
                onClick={() => {
                  if (request.kind === 'confirm') {
                    request.resolve(true);
                    setRequest(null);
                  } else if (request.kind === 'notify') {
                    request.resolve();
                    setRequest(null);
                  } else if (request.kind === 'prompt') {
                    submitPrompt();
                  }
                }}
              >
                {request.kind === 'notify'
                  ? 'OK'
                  : request.kind === 'confirm'
                    ? (request.options.confirmLabel ?? 'Confirm')
                    : (request.options.confirmLabel ?? 'Save')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function WarningGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    >
      <path d="M9 2.5 16 15H2L9 2.5Z" strokeLinejoin="round" />
      <path d="M9 7v4" strokeLinecap="round" />
      <circle cx="9" cy="13" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}
